import { Link } from "react-router-dom";
import { FileText, Mail, Phone } from "lucide-react";

const UPDATED = "June 1, 2026";
const COMPANY = "Kavuma Technologies LLC, DBA RideStamp";

const SECTIONS = [
  {
    id: "acceptance",
    title: "1. Acceptance of Terms",
    body: [
      `These Terms of Service ("Terms") are a legal agreement between you and ${COMPANY} ("RideStamp," "we," "us," or "our") governing your access to and use of our websites, web portal, APIs, and related services (collectively, the "Services").`,
      "By accessing or using the Services, creating an account, or clicking to accept these Terms, you agree to be bound by them. If you are entering into these Terms on behalf of an organization, you represent that you have authority to bind that organization, and \"you\" refers to that organization.",
    ],
  },
  {
    id: "services",
    title: "2. The Services",
    body: [
      "RideStamp provides an operations and accountability platform for non-emergency and non-medical transportation providers, including fleet and driver management, dispatch, live GPS visibility, incident and compliance tracking, and reporting.",
      "We may add, modify, or discontinue features from time to time. We will use commercially reasonable efforts to notify you of material changes that affect your use of the Services.",
    ],
  },
  {
    id: "accounts",
    title: "3. Accounts, Roles & Security",
    list: [
      "You must provide accurate information and keep your account credentials confidential.",
      "You are responsible for all activity under your account and for your personnel's use of assigned roles (Owner, Supervisor, Dispatcher, Administrator).",
      "You must promptly notify us of any unauthorized access or suspected security breach.",
      "You are responsible for ensuring that only authorized users within your organization access your data.",
    ],
  },
  {
    id: "acceptable-use",
    title: "4. Acceptable Use",
    body: ["You agree not to:"],
    list: [
      "Use the Services for any unlawful, fraudulent, or harmful purpose.",
      "Attempt to gain unauthorized access to the Services, other organizations' data, or our systems.",
      "Interfere with or disrupt the integrity or performance of the Services.",
      "Reverse engineer, scrape, or resell the Services except as expressly permitted.",
      "Upload malicious code or content that infringes the rights of others.",
    ],
  },
  {
    id: "customer-data",
    title: "5. Your Data",
    body: [
      "You retain all rights to the operational data you and your personnel submit to the Services (\"Customer Data\"), including drivers, vehicles, trips, incidents, and compliance records.",
      "You grant RideStamp a limited license to host, process, and display Customer Data solely to provide and improve the Services. Our handling of personal information is described in our Privacy Policy.",
      "You are responsible for the accuracy of Customer Data and for obtaining any consents required to collect and process it, including location/GPS data about your drivers.",
    ],
  },
  {
    id: "fees",
    title: "6. Fees & Payment",
    body: [
      "Access to paid features is subject to the fees and billing terms presented at the time of purchase or in your order. Unless stated otherwise, fees are non-refundable and exclusive of applicable taxes.",
      "If you fail to pay amounts when due, we may suspend or terminate access to the Services after reasonable notice.",
    ],
  },
  {
    id: "ip",
    title: "7. Intellectual Property",
    body: [
      "The Services, including all software, designs, text, graphics, and the RideStamp name and logos, are owned by RideStamp or its licensors and are protected by intellectual property laws. Except for the limited rights expressly granted to you, no rights are transferred.",
    ],
  },
  {
    id: "third-party",
    title: "8. Third-Party Services",
    body: [
      "The Services may integrate with third-party services (such as mapping and location providers). Your use of those services may be subject to separate terms, and we are not responsible for third-party services.",
    ],
  },
  {
    id: "disclaimers",
    title: "9. Disclaimers",
    body: [
      "THE SERVICES ARE PROVIDED \"AS IS\" AND \"AS AVAILABLE\" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. We do not warrant that the Services will be uninterrupted, error-free, or that location data will be accurate at all times.",
    ],
  },
  {
    id: "liability",
    title: "10. Limitation of Liability",
    body: [
      "TO THE MAXIMUM EXTENT PERMITTED BY LAW, RIDESTAMP WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR LOST PROFITS OR DATA, ARISING FROM OR RELATED TO THE SERVICES. OUR TOTAL LIABILITY FOR ANY CLAIM WILL NOT EXCEED THE AMOUNTS YOU PAID TO US FOR THE SERVICES IN THE TWELVE MONTHS PRECEDING THE CLAIM.",
    ],
  },
  {
    id: "indemnity",
    title: "11. Indemnification",
    body: [
      "You agree to indemnify and hold RideStamp harmless from any claims, damages, and expenses (including reasonable legal fees) arising from your use of the Services, your Customer Data, or your violation of these Terms or applicable law.",
    ],
  },
  {
    id: "termination",
    title: "12. Term & Termination",
    body: [
      "These Terms remain in effect while you use the Services. You may stop using the Services at any time. We may suspend or terminate access if you breach these Terms or if required to protect the Services or comply with law.",
      "Upon termination, your right to use the Services ends. Sections that by their nature should survive (including ownership, disclaimers, limitation of liability, and indemnification) will survive.",
    ],
  },
  {
    id: "law",
    title: "13. Governing Law & Disputes",
    body: [
      "These Terms are governed by the laws of the United States and the state in which Kavuma Technologies LLC is organized, without regard to conflict-of-laws principles. The parties agree to resolve disputes in the courts located in that jurisdiction, unless otherwise required by applicable law.",
    ],
  },
  {
    id: "changes",
    title: "14. Changes to These Terms",
    body: [
      "We may update these Terms from time to time. We will revise the \"Last updated\" date above and, where appropriate, provide additional notice. Your continued use of the Services after changes become effective constitutes acceptance of the revised Terms.",
    ],
  },
];

export default function TermsPage() {
  return (
    <div data-testid="terms-page">
      <div className="bg-slate-950 pt-36 pb-16 text-white">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-blue-300">
            <FileText className="h-4 w-4" /> Legal
          </div>
          <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight sm:text-5xl">Terms of Service</h1>
          <p className="mt-4 text-slate-400">Last updated: {UPDATED}</p>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8 lg:py-20">
        <div className="mb-10 rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900" data-testid="terms-disclaimer">
          <strong>Template notice:</strong> These Terms of Service are a comprehensive starting draft for {COMPANY}. Please have them reviewed by qualified legal counsel to ensure they fit your specific operations and applicable laws before relying on them.
        </div>

        <div className="space-y-10">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} className="scroll-mt-28" data-testid={`terms-section-${s.id}`}>
              <h2 className="font-display text-xl font-bold text-slate-900 lg:text-2xl">{s.title}</h2>
              {s.body?.map((p, i) => (
                <p key={i} className="mt-3 text-base leading-relaxed text-slate-600">{p}</p>
              ))}
              {s.list && (
                <ul className="mt-4 space-y-2.5">
                  {s.list.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-blue-600" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          <section id="contact" className="scroll-mt-28" data-testid="terms-section-contact">
            <h2 className="font-display text-xl font-bold text-slate-900 lg:text-2xl">15. Contact Us</h2>
            <p className="mt-3 text-base leading-relaxed text-slate-600">
              If you have questions about these Terms, contact us:
            </p>
            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <p className="font-display text-base font-bold text-slate-900">{COMPANY}</p>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <a href="mailto:legal@myridestamp.com" data-testid="terms-contact-email" className="flex items-center gap-2.5 hover:text-blue-700"><Mail className="h-4 w-4 text-blue-700" /> legal@myridestamp.com</a>
                <div className="flex items-center gap-2.5"><Phone className="h-4 w-4 text-blue-700" /> (339) 234-4636</div>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 border-t border-slate-200 pt-8 text-sm text-slate-500">
          See also our <Link to="/privacy" className="font-semibold text-blue-700 hover:underline" data-testid="terms-privacy-link">Privacy Policy</Link>.
        </div>
      </div>
    </div>
  );
}
